import {
  Ban,
  BellRing,
  Check,
  ChevronRight,
  MessageSquareWarning,
  RefreshCw,
  Search,
  ShieldAlert,
  TimerReset,
  UserRoundCheck,
  UserRoundX,
  Users,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  loadChannelRoles,
  loadChannelState,
  loadChatSearch,
  loadCurrentViewers,
  loadModerationActivity,
  loadModerationConsoleStatus,
  loadModerationStatus,
  loadNeedsAttention,
  loadShoutoutSettings,
  loadViewerDetail,
  loadViewerHistory,
  loadViewerLocalState,
  mutateViewer,
  sendManualShoutout,
  setAutoShoutouts,
  ViewerApiError,
  type ActivityEntry,
  type ChannelRoles,
  type ChannelState,
  type ChatSearchResponse,
  type ModerationConsoleStatus,
  type ModerationStatus,
  type NeedsAttention,
  type ShoutoutSettings,
  type ViewerDetail,
  type ViewerHistoryResponse,
  type ViewerLocalState,
  type ViewerSummary,
} from './viewers-api';
import './viewers.css';

const WARNING_PRESETS = [
  ['Spam', 'Please stop spamming or flooding chat.'],
  ['Respect', 'Please keep chat respectful and stop personal attacks.'],
  ['Hate', 'Please stop posting hateful or discriminatory content.'],
  ['Spoilers', 'Please stop posting spoilers without permission.'],
  ['Rules', 'Please follow the channel chat rules.'],
] as const;

function formatWhen(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? date.toLocaleString('en-GB') : '—';
}

function formatKnownFor(value: string | null | undefined): string {
  if (!value) return '—';
  const start = new Date(value).getTime();
  if (!Number.isFinite(start)) return '—';
  const days = Math.max(0, Math.floor((Date.now() - start) / 86_400_000));
  if (days >= 365) {
    const years = Math.floor(days / 365);
    const months = Math.floor((days % 365) / 30);
    return `${years}y${months ? ` ${months}m` : ''}`;
  }
  if (days >= 30) return `${Math.floor(days / 30)} month${Math.floor(days / 30) === 1 ? '' : 's'}`;
  return `${days} day${days === 1 ? '' : 's'}`;
}

function activityLabel(action: string): string {
  return action
    .replace(/^channel-role\./, '')
    .replace(/^channel\./, '')
    .replace(/^twitch\./, '')
    .replaceAll('.', ' ')
    .replaceAll('-', ' ')
    .toUpperCase();
}

function statusTone(enabled: boolean): string {
  return enabled ? 'good' : 'bad';
}

export function ViewersPage() {
  const [status, setStatus] = useState<ModerationStatus | null>(null);
  const [consoleStatus, setConsoleStatus] = useState<ModerationConsoleStatus | null>(null);
  const [channel, setChannel] = useState<ChannelState | null>(null);
  const [viewers, setViewers] = useState<ViewerSummary[]>([]);
  const [selectedId, setSelectedId] = useState('');
  const [detail, setDetail] = useState<ViewerDetail | null>(null);
  const [localState, setLocalState] = useState<ViewerLocalState | null>(null);
  const [attention, setAttention] = useState<NeedsAttention | null>(null);
  const [activity, setActivity] = useState<ActivityEntry[]>([]);
  const [roles, setRoles] = useState<ChannelRoles | null>(null);
  const [shoutouts, setShoutouts] = useState<ShoutoutSettings | null>(null);
  const [history, setHistory] = useState<ViewerHistoryResponse | null>(null);
  const [chat, setChat] = useState<ChatSearchResponse | null>(null);
  const [currentSearch, setCurrentSearch] = useState('');
  const [historySearch, setHistorySearch] = useState('');
  const [chatSearch, setChatSearch] = useState('');
  const [historyUserId, setHistoryUserId] = useState('');
  const [historyOffset, setHistoryOffset] = useState(0);
  const [reason, setReason] = useState('');
  const [warningReason, setWarningReason] = useState('');
  const [customMinutes, setCustomMinutes] = useState('');
  const [slowCustom, setSlowCustom] = useState('');
  const [followersCustom, setFollowersCustom] = useState('');
  const [noteDraft, setNoteDraft] = useState('');
  const [watchReason, setWatchReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [loadingViewer, setLoadingViewer] = useState(false);
  const [message, setMessage] = useState('');
  const selectionSequence = useRef(0);

  const refreshOverview = useCallback(async () => {
    const results = await Promise.allSettled([
      loadModerationStatus(),
      loadModerationConsoleStatus(),
      loadCurrentViewers(),
      loadChannelState(),
      loadNeedsAttention(),
      loadModerationActivity(),
      loadChannelRoles(),
      loadShoutoutSettings(),
    ]);

    if (results[0].status === 'fulfilled') setStatus(results[0].value);
    if (results[1].status === 'fulfilled') setConsoleStatus(results[1].value);
    if (results[2].status === 'fulfilled') setViewers(results[2].value.viewers ?? []);
    if (results[3].status === 'fulfilled') setChannel(results[3].value);
    if (results[4].status === 'fulfilled') setAttention(results[4].value);
    if (results[5].status === 'fulfilled') setActivity(results[5].value.entries ?? []);
    if (results[6].status === 'fulfilled') setRoles(results[6].value);
    if (results[7].status === 'fulfilled') setShoutouts(results[7].value);

    const firstFailure = results.find((result) => result.status === 'rejected');
    if (firstFailure?.status === 'rejected') {
      const error = firstFailure.reason;
      if (!(error instanceof ViewerApiError && (error.status === 409 || error.status === 401))) {
        setMessage(error instanceof Error ? error.message : 'Some viewer data could not be loaded.');
      }
    }
  }, []);

  const selectViewer = useCallback(async (id: string) => {
    const sequence = ++selectionSequence.current;
    setSelectedId(id);
    setLoadingViewer(true);
    try {
      const [nextDetail, nextLocal] = await Promise.all([loadViewerDetail(id), loadViewerLocalState(id)]);
      if (sequence !== selectionSequence.current) return;
      setDetail(nextDetail);
      setLocalState(nextLocal);
      setWatchReason(nextLocal.watch.active ? nextLocal.watch.reason ?? '' : '');
      setMessage('');
    } catch (error) {
      if (sequence !== selectionSequence.current) return;
      setDetail(null);
      setLocalState(null);
      setMessage(error instanceof Error ? error.message : 'Viewer details could not be loaded.');
    } finally {
      if (sequence === selectionSequence.current) setLoadingViewer(false);
    }
  }, []);

  const refreshSelected = useCallback(async () => {
    if (selectedId) await selectViewer(selectedId);
  }, [selectViewer, selectedId]);

  useEffect(() => {
    void refreshOverview();
    const timer = window.setInterval(() => void refreshOverview(), 15_000);
    return () => {
      window.clearInterval(timer);
      selectionSequence.current += 1;
    };
  }, [refreshOverview]);

  const visibleViewers = useMemo(() => {
    const query = currentSearch.trim().toLocaleLowerCase('en-GB');
    if (!query) return viewers;
    return viewers.filter((viewer) => `${viewer.name} ${viewer.login}`.toLocaleLowerCase('en-GB').includes(query));
  }, [currentSearch, viewers]);

  const selectedIsModerator = Boolean(selectedId && roles?.moderators.some((entry) => entry.user_id === selectedId));
  const selectedIsVip = Boolean(selectedId && roles?.vips.some((entry) => entry.user_id === selectedId));
  const baseReady = Boolean(status?.connected && status.banScopeReady && status.chatScopeReady);

  async function runMutation(
    path: string,
    options: {
      method?: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
      payload?: Record<string, unknown>;
      confirm?: string;
      success: string;
    },
  ) {
    if (options.confirm && !window.confirm(options.confirm)) return false;
    setBusy(true);
    setMessage('');
    try {
      await mutateViewer(path, options.method ?? 'POST', options.payload);
      setMessage(options.success);
      await refreshOverview();
      await refreshSelected();
      return true;
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Viewer action failed.');
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function setMode(setting: 'slow' | 'followers' | 'subscribers' | 'emote' | 'unique', enabled: boolean, value?: number) {
    await runMutation('/api/v1/moderation/channel-settings', {
      payload: { setting, enabled, ...(value === undefined ? {} : { value }) },
      confirm: `${enabled ? 'Enable' : 'Disable'} ${setting === 'followers' ? 'followers-only' : setting} mode?`,
      success: 'Twitch channel setting updated.',
    });
  }

  async function setShield(enabled: boolean) {
    await runMutation('/api/v1/moderation/shield', {
      payload: { enabled },
      confirm: `${enabled ? 'Enable' : 'Disable'} Twitch Shield Mode?`,
      success: 'Shield Mode updated.',
    });
  }

  async function loadHistory(offset = 0) {
    setBusy(true);
    try {
      const next = await loadViewerHistory(historySearch, offset);
      setHistory(next);
      setHistoryOffset(offset);
      setMessage('');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Viewer history could not be loaded.');
    } finally {
      setBusy(false);
    }
  }

  async function searchChat() {
    setBusy(true);
    try {
      setChat(await loadChatSearch(chatSearch, historyUserId));
      setMessage('');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Chat search could not be loaded.');
    } finally {
      setBusy(false);
    }
  }

  async function addNote() {
    if (!detail || !noteDraft.trim()) return;
    const ok = await runMutation(`/api/v1/moderation/users/${encodeURIComponent(detail.viewer.id)}/notes`, {
      payload: { text: noteDraft.trim() },
      success: 'Private moderator note added.',
    });
    if (ok) setNoteDraft('');
  }

  async function editNote(note: ViewerLocalState['notes'][number]) {
    const text = window.prompt('Edit private moderator note:', note.text);
    if (text === null || !text.trim() || text.trim() === note.text) return;
    await runMutation(`/api/v1/moderation/notes/${encodeURIComponent(note.id)}`, {
      method: 'PATCH',
      payload: { text: text.trim() },
      success: 'Private moderator note updated.',
    });
  }

  async function saveWatch() {
    if (!detail || !watchReason.trim()) return;
    await runMutation(`/api/v1/moderation/users/${encodeURIComponent(detail.viewer.id)}/watch`, {
      method: 'PUT',
      payload: { reason: watchReason.trim() },
      success: 'Private FFSControl watch flag saved. No Twitch action was taken.',
    });
  }

  async function toggleAutoShoutout() {
    if (!shoutouts) return;
    setBusy(true);
    try {
      const next = await setAutoShoutouts(!shoutouts.autoRaiders);
      setShoutouts(next);
      setMessage(next.autoRaiders ? 'Automatic raider shoutouts enabled.' : 'Automatic raider shoutouts disabled.');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Shoutout setting could not be changed.');
    } finally {
      setBusy(false);
    }
  }

  async function manualShoutout() {
    if (!detail) return;
    setBusy(true);
    try {
      const result = await sendManualShoutout(detail.viewer.id, detail.viewer.login);
      setMessage(`${result.target.name}: visual ${result.visual.status} · chat ${result.chat.status} · Twitch ${result.twitch.status}.`);
      await refreshOverview();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Shoutout could not be started.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="ffs-workspace-page ffs-viewers-page">
      {message ? <div className="ffs-workspace-message">{message}</div> : null}

      <section className="ffs-workspace-hero">
        <div>
          <small>PEOPLE &amp; TWITCH SAFETY</small>
          <h2>Viewers</h2>
          <p>Current chatters, moderation, viewer history and audience context in one workspace.</p>
        </div>
        <div className="ffs-viewer-hero-actions">
          <span className={`ffs-status ${statusTone(status?.connected === true)}`}>{status?.connected ? 'TWITCH CONNECTED' : 'TWITCH CHECK'}</span>
          <span className={`ffs-status ${statusTone(consoleStatus?.channelControlsReady === true)}`}>{consoleStatus?.channelControlsReady ? 'CHANNEL READY' : 'CHANNEL SCOPES'}</span>
          <button type="button" className={`ffs-button ${shoutouts?.autoRaiders ? 'primary' : 'secondary'}`} disabled={busy || !shoutouts} onClick={() => void toggleAutoShoutout()}>
            <BellRing size={15} /> Auto raid shoutouts {shoutouts?.autoRaiders ? 'On' : 'Off'}{shoutouts?.pending ? ` · ${shoutouts.pending}` : ''}
          </button>
          <button type="button" className="ffs-button secondary" disabled={busy} onClick={() => void refreshOverview()}><RefreshCw size={15} /> Refresh</button>
        </div>
      </section>

      {status?.missingScopes.length && status.canAuthorise ? (
        <section className="ffs-viewer-auth-strip"><span>Base moderation permissions need authorising.</span><button className="ffs-button primary" type="button" onClick={() => window.location.assign('/api/v1/moderation/connect')}>Authorise Base Moderation</button></section>
      ) : null}
      {consoleStatus?.owner && consoleStatus.missingBotScopes.length ? (
        <section className="ffs-viewer-auth-strip"><span>Additional Twitch moderation permissions are missing.</span><button className="ffs-button primary" type="button" onClick={() => window.location.assign('/api/v1/moderation/authorise-console')}>Authorise Moderation</button></section>
      ) : null}
      {consoleStatus?.owner && consoleStatus.missingBroadcasterAdminScopes.length ? (
        <section className="ffs-viewer-auth-strip"><span>Moderator/VIP administration permissions are missing.</span><button className="ffs-button primary" type="button" onClick={() => window.location.assign('/api/v1/moderation/authorise-channel-admin')}>Authorise Role Admin</button></section>
      ) : null}

      <section className="ffs-workspace-card ffs-channel-safety-card">
        <header><div><small>CHANNEL SAFETY</small><h3>Live chat controls</h3></div><ShieldAlert size={18} /></header>
        <div className="ffs-channel-safety-grid">
          <ChannelMode title="Shield" state={channel?.shield.supported ? (channel.shield.active ? 'ON' : 'OFF') : 'UNAVAILABLE'} enabled={channel?.shield.supported === true && channel.shield.active}>
            <button type="button" className="ffs-button secondary" disabled={busy || !consoleStatus?.shieldReady || channel?.shield.supported !== true} onClick={() => void setShield(!(channel?.shield.supported && channel.shield.active))}>{channel?.shield.supported && channel.shield.active ? 'Disable' : 'Enable'}</button>
          </ChannelMode>
          <ChannelMode title="Slow Mode" state={channel?.chat.slowMode ? `${channel.chat.slowSeconds ?? '?'}s` : 'OFF'} enabled={channel?.chat.slowMode === true}>
            {[3, 5, 10, 30, 60, 120].map((seconds) => <button type="button" key={seconds} disabled={busy || !consoleStatus?.channelControlsReady} onClick={() => void setMode('slow', true, seconds)}>{seconds}s</button>)}
            <input aria-label="Custom Slow Mode seconds" type="number" min="3" max="120" value={slowCustom} onChange={(event) => setSlowCustom(event.target.value)} placeholder="sec" />
            <button type="button" disabled={busy || !consoleStatus?.channelControlsReady || Number(slowCustom) < 3 || Number(slowCustom) > 120} onClick={() => void setMode('slow', true, Number(slowCustom))}>Set</button>
            <button type="button" disabled={busy || !consoleStatus?.channelControlsReady || !channel?.chat.slowMode} onClick={() => void setMode('slow', false)}>Off</button>
          </ChannelMode>
          <ChannelMode title="Followers" state={channel?.chat.followersOnly ? `${channel.chat.followersMinutes ?? 0}m` : 'OFF'} enabled={channel?.chat.followersOnly === true}>
            {[0, 10, 30, 60, 1440, 10080].map((minutes) => <button type="button" key={minutes} disabled={busy || !consoleStatus?.channelControlsReady} onClick={() => void setMode('followers', true, minutes)}>{minutes === 0 ? 'Now' : minutes < 60 ? `${minutes}m` : minutes === 60 ? '1h' : minutes === 1440 ? '1d' : '7d'}</button>)}
            <input aria-label="Custom followers-only minutes" type="number" min="0" max="129600" value={followersCustom} onChange={(event) => setFollowersCustom(event.target.value)} placeholder="min" />
            <button type="button" disabled={busy || !consoleStatus?.channelControlsReady || followersCustom === '' || Number(followersCustom) < 0 || Number(followersCustom) > 129600} onClick={() => void setMode('followers', true, Number(followersCustom))}>Set</button>
            <button type="button" disabled={busy || !consoleStatus?.channelControlsReady || !channel?.chat.followersOnly} onClick={() => void setMode('followers', false)}>Off</button>
          </ChannelMode>
          {([
            ['subscribers', 'Subscribers', channel?.chat.subscribersOnly],
            ['emote', 'Emote Only', channel?.chat.emoteOnly],
            ['unique', 'Unique Chat', channel?.chat.uniqueChat],
          ] as const).map(([setting, label, enabled]) => (
            <ChannelMode title={label} state={enabled ? 'ON' : 'OFF'} enabled={enabled === true} key={setting}>
              <button type="button" className="ffs-button secondary" disabled={busy || !consoleStatus?.channelControlsReady} onClick={() => void setMode(setting, !enabled)}>{enabled ? 'Disable' : 'Enable'}</button>
            </ChannelMode>
          ))}
        </div>
      </section>

      <section className="ffs-viewer-live-grid">
        <article className="ffs-workspace-card ffs-current-viewers-card">
          <header><div><small>NOW IN CHAT</small><h3>Current viewers</h3></div><strong>{visibleViewers.length.toLocaleString('en-GB')}</strong></header>
          <label className="ffs-search-field"><Search size={15} /><input type="search" value={currentSearch} onChange={(event) => setCurrentSearch(event.target.value)} placeholder="Search current chatters" /></label>
          <div className="ffs-current-viewer-list">
            {visibleViewers.map((viewer) => (
              <button type="button" key={viewer.id} className={selectedId === viewer.id ? 'selected' : ''} onClick={() => void selectViewer(viewer.id)}>
                <div><strong>{viewer.name}</strong><span>@{viewer.login}</span><ViewerBadges viewer={viewer} /></div><ChevronRight size={14} />
              </button>
            ))}
            {visibleViewers.length === 0 ? <div className="ffs-empty-state"><Users size={16} /> No matching current viewers.</div> : null}
          </div>
        </article>

        <article className="ffs-workspace-card ffs-viewer-detail-card">
          {loadingViewer ? <div className="ffs-empty-state">Loading viewer…</div> : detail ? (
            <>
              <header>
                <div><small>SELECTED VIEWER</small><h3>{detail.viewer.name}</h3><p>@{detail.viewer.login} · Twitch ID {detail.viewer.id}</p></div>
                <div className="ffs-viewer-header-actions"><span className={`ffs-viewer-restriction ${detail.moderation.state}`}>{detail.moderation.state === 'none' ? 'NOT RESTRICTED' : detail.moderation.state.toUpperCase()}</span><button type="button" className="ffs-button secondary" disabled={busy} onClick={() => void manualShoutout()}>Shout Out</button></div>
              </header>
              <ViewerBadges viewer={detail.viewer} localWatch={localState?.watch.active === true} />
              <div className="ffs-viewer-facts">
                <div><span>Known for</span><strong>{formatKnownFor(detail.viewer.firstSeenAt)}</strong></div>
                <div><span>Last seen</span><strong>{formatWhen(detail.viewer.lastSeenAt)}</strong></div>
                <div><span>Messages</span><strong>{detail.viewer.messageCount.toLocaleString('en-GB')}</strong></div>
                <div><span>Restriction</span><strong>{detail.moderation.expiresAt ? `Until ${formatWhen(detail.moderation.expiresAt)}` : detail.moderation.reason ?? '—'}</strong></div>
              </div>

              {!baseReady && status ? <div className="ffs-viewer-notice danger">Timeout, ban and Twitch message-delete actions are locked until base moderation permissions are ready.</div> : null}

              <section className="ffs-viewer-section">
                <h4>Moderation</h4>
                <label><span>Reason</span><input value={reason} maxLength={500} onChange={(event) => setReason(event.target.value)} placeholder="Optional timeout/ban reason" /></label>
                <div className="ffs-viewer-action-row">
                  {[['1 min', 60], ['10 min', 600], ['30 min', 1800], ['1 hour', 3600], ['1 day', 86400], ['1 week', 604800]].map(([label, seconds]) => <button type="button" className="ffs-button secondary" key={String(label)} disabled={busy || !status?.banScopeReady} onClick={() => void runMutation('/api/v1/moderation/timeout', { payload: { userId: detail.viewer.id, durationSeconds: seconds, reason }, confirm: `Timeout ${detail.viewer.name} for ${label}?`, success: `${detail.viewer.name} timed out.` })}><TimerReset size={13} /> {label}</button>)}
                  <input className="ffs-compact-number" type="number" min="1" max="20160" value={customMinutes} onChange={(event) => setCustomMinutes(event.target.value)} placeholder="Custom min" />
                  <button type="button" className="ffs-button secondary" disabled={busy || !status?.banScopeReady || !Number.isInteger(Number(customMinutes)) || Number(customMinutes) < 1 || Number(customMinutes) > 20160} onClick={() => void runMutation('/api/v1/moderation/timeout', { payload: { userId: detail.viewer.id, durationSeconds: Number(customMinutes) * 60, reason }, confirm: `Timeout ${detail.viewer.name} for ${customMinutes} minute(s)?`, success: `${detail.viewer.name} timed out.` })}>Custom Timeout</button>
                  <button type="button" className="ffs-button danger" disabled={busy || !status?.banScopeReady || detail.moderation.state === 'banned'} onClick={() => void runMutation('/api/v1/moderation/ban', { payload: { userId: detail.viewer.id, reason }, confirm: `Ban ${detail.viewer.name} indefinitely?`, success: `${detail.viewer.name} banned.` })}><Ban size={13} /> Ban</button>
                  <button type="button" className="ffs-button secondary" disabled={busy || !status?.banScopeReady || detail.moderation.state === 'none'} onClick={() => void runMutation('/api/v1/moderation/unban', { payload: { userId: detail.viewer.id }, confirm: `${detail.moderation.state === 'timeout' ? 'End the timeout for' : 'Unban'} ${detail.viewer.name}?`, success: `${detail.viewer.name} restriction cleared.` })}><UserRoundX size={13} /> {detail.moderation.state === 'timeout' ? 'End Timeout' : 'Unban'}</button>
                </div>
                <div className="ffs-warning-presets">{WARNING_PRESETS.map(([label, text]) => <button type="button" key={label} onClick={() => setWarningReason(text)}>{label}</button>)}</div>
                <div className="ffs-viewer-action-row"><input className="grow" value={warningReason} maxLength={500} onChange={(event) => setWarningReason(event.target.value)} placeholder="Warning preset or custom reason" /><button type="button" className="ffs-button warning" disabled={busy || !consoleStatus?.warnReady || !warningReason.trim()} onClick={() => void runMutation('/api/v1/moderation/warn', { payload: { userId: detail.viewer.id, reason: warningReason.trim() }, confirm: `Send a Twitch warning to ${detail.viewer.name}?`, success: 'Twitch warning sent.' })}><MessageSquareWarning size={13} /> Warn</button></div>
                <div className="ffs-viewer-notice">Twitch does not currently provide a supported “purge every message from this user” Helix mutation. FFSControl will not fake it by scraping Twitch; individual recent messages can be deleted below.</div>
              </section>

              <section className="ffs-viewer-section">
                <h4>Recent chat messages</h4>
                <div className="ffs-viewer-message-list">
                  {detail.messages.length === 0 ? <div className="ffs-empty-state">No recent messages recorded.</div> : detail.messages.map((item) => (
                    <div className={item.deletedAt ? 'deleted' : ''} key={item.id}><div><small>{formatWhen(item.sentAt)}</small><p>{item.text}</p></div>{item.deletedAt ? <span>Deleted</span> : <button type="button" className="ffs-button danger" disabled={busy || !status?.chatScopeReady} onClick={() => void runMutation('/api/v1/moderation/delete-message', { payload: { messageId: item.id }, confirm: `Delete this Twitch chat message from ${detail.viewer.name}?`, success: 'Twitch message deleted.' })}>Delete</button>}</div>
                  ))}
                </div>
              </section>

              <section className="ffs-viewer-section local-section">
                <h4>Private FFSControl notes &amp; watch</h4>
                <div className="ffs-viewer-notice local">These controls only change private FFSControl state. They never warn, timeout, ban or otherwise change Twitch.</div>
                <div className="ffs-viewer-action-row"><input className="grow" value={watchReason} maxLength={500} onChange={(event) => setWatchReason(event.target.value)} placeholder="Private watch reason" /><button type="button" className="ffs-button secondary" disabled={busy || !watchReason.trim()} onClick={() => void saveWatch()}>{localState?.watch.active ? 'Update Watch' : 'Set Watch'}</button><button type="button" className="ffs-button secondary" disabled={busy || !localState?.watch.active} onClick={() => void runMutation(`/api/v1/moderation/users/${encodeURIComponent(detail.viewer.id)}/watch`, { method: 'DELETE', confirm: `Clear the private watch flag for ${detail.viewer.name}?`, success: 'Private watch flag cleared.' })}>Clear Watch</button></div>
                <div className="ffs-viewer-action-row"><textarea className="grow" rows={2} value={noteDraft} maxLength={1000} onChange={(event) => setNoteDraft(event.target.value)} placeholder="Add a private moderator note" /><button type="button" className="ffs-button secondary" disabled={busy || !noteDraft.trim()} onClick={() => void addNote()}>Add Note</button></div>
                <div className="ffs-note-list">{localState?.notes.map((note) => <div key={note.id}><p>{note.text}</p><small>{note.actor} · {formatWhen(note.createdAt)}</small><div><button type="button" onClick={() => void editNote(note)}>Edit</button><button type="button" onClick={() => void runMutation(`/api/v1/moderation/notes/${encodeURIComponent(note.id)}`, { method: 'DELETE', confirm: 'Delete this private moderator note?', success: 'Private moderator note deleted.' })}>Delete</button></div></div>)}</div>
              </section>

              <section className="ffs-viewer-section">
                <h4>Moderator / VIP roles</h4>
                <div className="ffs-viewer-action-row"><span className={`ffs-role-pill ${selectedIsModerator ? 'active' : ''}`}>MOD {selectedIsModerator ? 'YES' : 'NO'}</span><span className={`ffs-role-pill ${selectedIsVip ? 'active' : ''}`}>VIP {selectedIsVip ? 'YES' : 'NO'}</span><button type="button" className="ffs-button secondary" disabled={busy || !consoleStatus?.owner || !consoleStatus.moderatorAdminReady} onClick={() => void runMutation(`/api/v1/moderation/channel-roles/moderator/${encodeURIComponent(detail.viewer.id)}`, { method: selectedIsModerator ? 'DELETE' : 'POST', confirm: `${selectedIsModerator ? 'Remove' : 'Add'} Twitch moderator role for ${detail.viewer.name}?`, success: 'Moderator role updated.' })}>{selectedIsModerator ? 'Remove Mod' : 'Add Mod'}</button><button type="button" className="ffs-button secondary" disabled={busy || !consoleStatus?.owner || !consoleStatus.vipAdminReady} onClick={() => void runMutation(`/api/v1/moderation/channel-roles/vip/${encodeURIComponent(detail.viewer.id)}`, { method: selectedIsVip ? 'DELETE' : 'POST', confirm: `${selectedIsVip ? 'Remove' : 'Add'} Twitch VIP role for ${detail.viewer.name}?`, success: 'VIP role updated.' })}>{selectedIsVip ? 'Remove VIP' : 'Add VIP'}</button></div>
              </section>
            </>
          ) : <div className="ffs-empty-state"><UserRoundCheck size={17} /> Select a viewer to see history, Twitch state and moderation controls.</div>}
        </article>

        <aside className="ffs-viewer-rail">
          <article className="ffs-workspace-card">
            <header><div><small>ACTION QUEUE</small><h3>Needs attention</h3></div><strong>{(attention?.automod.messages.length ?? 0) + (attention?.recentFailures.length ?? 0)}</strong></header>
            <div className="ffs-attention-list">
              {!attention?.automod.supported ? <div className="ffs-viewer-notice">AutoMod review is unavailable until its supported Twitch permission/event path is authorised.</div> : null}
              {attention?.automod.messages.map((item) => <div className="ffs-attention-item held" key={item.messageId}><button type="button" className="identity" onClick={() => void selectViewer(item.userId)}><strong>{item.name}</strong><small>{formatWhen(item.heldAt)} · {item.reason === 'blocked_term' ? 'blocked term' : item.category ?? 'AutoMod'}</small><p>{item.text}</p></button><div><button type="button" disabled={busy} onClick={() => void runMutation(`/api/v1/moderation/automod/${encodeURIComponent(item.messageId)}/decision`, { payload: { action: 'allow' }, confirm: `Allow this held message from ${item.name}?`, success: 'Held message allowed.' })}><Check size={13} /> Allow</button><button type="button" className="deny" disabled={busy} onClick={() => void runMutation(`/api/v1/moderation/automod/${encodeURIComponent(item.messageId)}/decision`, { payload: { action: 'deny' }, confirm: `Deny this held message from ${item.name}?`, success: 'Held message denied.' })}><X size={13} /> Deny</button></div></div>)}
              {attention?.recentFailures.map((item) => <button type="button" className="ffs-attention-item failure" key={item.id} onClick={() => void selectViewer(item.userId)}><strong>{item.viewer} · {activityLabel(item.action)}</strong><small>{formatWhen(item.createdAt)} · {item.actor}</small><p>{item.reason ?? String(item.metadata.error ?? 'Moderation action failed.')}</p></button>)}
              {attention?.automod.supported && !attention.automod.messages.length && !attention.recentFailures.length ? <div className="ffs-empty-state">Nothing currently needs moderation attention.</div> : null}
            </div>
          </article>

          <article className="ffs-workspace-card">
            <header><div><small>AUDIT TRAIL</small><h3>Recent activity</h3></div><strong>{activity.length}</strong></header>
            <div className="ffs-activity-list">{activity.slice(0, 30).map((item) => <button type="button" key={item.id} onClick={() => void selectViewer(item.userId)}><strong>{item.viewer}</strong><span>{activityLabel(item.action)} · {item.result}</span><small>{formatWhen(item.createdAt)} · {item.actor}</small></button>)}</div>
          </article>
        </aside>
      </section>

      <section id="viewer-history" className="ffs-history-grid">
        <article className="ffs-workspace-card">
          <header><div><small>LONG-TERM AUDIENCE DATA</small><h3>Viewer history</h3></div><strong>{history?.total?.toLocaleString('en-GB') ?? '—'}</strong></header>
          <form className="ffs-history-search" onSubmit={(event) => { event.preventDefault(); void loadHistory(0); }}><Search size={15} /><input value={historySearch} onChange={(event) => setHistorySearch(event.target.value)} placeholder="Search viewers by name or login" /><button type="submit" className="ffs-button primary" disabled={busy}>Search</button></form>
          <div className="ffs-history-list">
            {history?.viewers.map((viewer) => <button type="button" key={viewer.user_id} onClick={() => { setHistoryUserId(viewer.user_id); void selectViewer(viewer.user_id); }}><div><strong>{viewer.display_name}</strong><span>@{viewer.user_login}</span></div><div><small>Known {formatKnownFor(viewer.first_seen_at)}</small><small>{Number(viewer.message_count).toLocaleString('en-GB')} messages · {Number(viewer.welcome_count).toLocaleString('en-GB')} welcomes</small></div><ChevronRight size={14} /></button>)}
            {!history ? <div className="ffs-empty-state">Search the retained viewer history when you need older audience context.</div> : history.viewers.length === 0 ? <div className="ffs-empty-state">No viewers matched this search.</div> : null}
          </div>
          {history ? <div className="ffs-history-pager"><button type="button" className="ffs-button secondary" disabled={busy || historyOffset === 0} onClick={() => void loadHistory(Math.max(0, historyOffset - history.limit))}>Previous</button><span>{(historyOffset + 1).toLocaleString('en-GB')}–{Math.min(history.total, historyOffset + history.viewers.length).toLocaleString('en-GB')} of {history.total.toLocaleString('en-GB')}</span><button type="button" className="ffs-button secondary" disabled={busy || !history.hasMore} onClick={() => void loadHistory(historyOffset + history.limit)}>Next</button></div> : null}
        </article>

        <article className="ffs-workspace-card">
          <header><div><small>SEARCHABLE CHAT LOG</small><h3>Chat history</h3></div><span>{historyUserId ? 'Viewer filtered' : 'All viewers'}</span></header>
          <form className="ffs-history-search" onSubmit={(event) => { event.preventDefault(); void searchChat(); }}><Search size={15} /><input value={chatSearch} onChange={(event) => setChatSearch(event.target.value)} placeholder="Search retained chat messages" /><button type="submit" className="ffs-button primary" disabled={busy}>Search</button>{historyUserId ? <button type="button" className="ffs-button secondary" onClick={() => setHistoryUserId('')}>Clear Viewer</button> : null}</form>
          <div className="ffs-chat-history-list">{chat?.messages.map((item) => <button type="button" key={item.message_id} onClick={() => { setHistoryUserId(item.user_id); void selectViewer(item.user_id); }}><div><strong>{item.user_name}</strong><small>@{item.user_login} · {formatWhen(item.sent_at)}</small></div><p>{item.message_text}</p></button>)}{!chat ? <div className="ffs-empty-state">Search retained chat by text, optionally scoped to a selected historical viewer.</div> : chat.messages.length === 0 ? <div className="ffs-empty-state">No chat messages matched this search.</div> : null}</div>
        </article>
      </section>
    </div>
  );
}

function ViewerBadges({ viewer, localWatch = false }: { viewer: Pick<ViewerSummary, 'isBroadcaster' | 'isModerator' | 'isVip' | 'isSubscriber' | 'subscriptionTier'>; localWatch?: boolean }) {
  return <div className="ffs-viewer-badges">{viewer.isBroadcaster ? <span className="broadcaster">BROADCASTER</span> : null}{viewer.isModerator ? <span className="mod">MOD</span> : null}{viewer.isVip ? <span className="vip">VIP</span> : null}{viewer.isSubscriber ? <span className="sub">SUB{viewer.subscriptionTier ? ` · ${viewer.subscriptionTier}` : ''}</span> : null}{localWatch ? <span className="watch">PRIVATE WATCH</span> : null}</div>;
}

function ChannelMode({ title, state, enabled, children }: { title: string; state: string; enabled: boolean; children: React.ReactNode }) {
  return <div className={`ffs-channel-mode ${enabled ? 'active' : ''}`}><div><span>{title}</span><strong>{state}</strong></div><div className="controls">{children}</div></div>;
}
